import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Newspaper, ExternalLink, MessageSquare, Send, Loader2, CheckCircle2 } from "lucide-react";
import { ageLabel } from "../stream/streamModel";

export const NewsItems = ({ items }) => items.length > 0 && (
  <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4" data-testid="news-items">
    <h3 className="text-xs font-medium text-ism-teal uppercase tracking-wider mb-1 flex items-center gap-2">
      <Newspaper className="h-3.5 w-3.5" />
      News & Trigger Events ({items.length})
    </h3>
    <p className="text-gray-500 text-xs mb-3">Published within the last 7 days — possible buying signals or outreach openers</p>
    <div className="space-y-2">
      {items.map((news, idx) => {
        const age = ageLabel(news.publishedAt || news.date);
        return (
          <a key={news.url || news.link || news.title || idx} href={news.url} target="_blank" rel="noopener noreferrer"
            className="block rounded-lg bg-white/5 p-3 border border-white/5 hover:border-ism-teal/40 transition-colors" data-testid={`news-item-${idx}`}>
            <h4 className="text-white text-sm font-medium flex items-start gap-2">
              <span className="flex-1">{news.title}</span>
              <ExternalLink className="h-3.5 w-3.5 text-ism-teal flex-shrink-0 mt-0.5" />
            </h4>
            {news.snippet && <p className="text-gray-400 text-xs mt-1 line-clamp-2">{news.snippet}</p>}
            <p className="text-xs text-gray-500 mt-2">
              {news.source || "Source"}{age ? <span className="text-ism-teal"> · {age}</span> : ""}
            </p>
          </a>
        );
      })}
    </div>
  </div>
);

export const needsReply = (insights) => /QUESTION FOR YOU|HELP ME BUILD|HELP ME FIND|HELP ME IDENTIFY/i.test(insights || "");

const hostOf = (url) => { try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; } };

// Web sources the research/competitor insight was built from (clickable).
export const SourceList = ({ sources }) => sources.length > 0 && (
  <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4" data-testid="insight-sources">
    <h3 className="text-xs font-medium text-ism-teal uppercase tracking-wider mb-2">Sources</h3>
    <ul className="space-y-1.5">
      {sources.filter((s) => s.url).map((s, idx) => (
        <li key={s.url}>
          <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-300 hover:text-ism-teal flex items-start gap-2" data-testid={`insight-source-${idx}`}>
            <ExternalLink className="h-3.5 w-3.5 text-ism-teal flex-shrink-0 mt-0.5" />
            <span className="min-w-0"><span className="line-clamp-1">{s.title || s.url}</span><span className="text-xs text-gray-500">{s.source || hostOf(s.url)}</span></span>
          </a>
        </li>
      ))}
    </ul>
  </div>
);

export const InsightReply = ({ text, setText, isReplying, onSubmit }) => (
  <div className="mt-4 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20" data-testid="insight-reply-section">
    <h4 className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-2"><MessageSquare className="h-4 w-4" />Reply to ISM</h4>
    <Textarea
      value={text}
      onChange={(e) => setText(e.target.value)}
      placeholder="Type your answer here — the more detail you give, the better ISM can tailor your strategy..."
      rows={3}
      className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 text-sm resize-none mb-3"
      data-testid="insight-reply-input"
    />
    <Button onClick={onSubmit} disabled={isReplying || !text.trim()} className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="insight-reply-submit">
      {isReplying ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</> : <><Send className="h-4 w-4 mr-2" /> Send Response</>}
    </Button>
  </div>
);

export const ReplyAppliedBanner = () => (
  <div className="mt-2 mb-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center gap-2">
    <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
    <span className="text-sm text-green-400">Strategy updated based on your input</span>
  </div>
);
